import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Activity, Users, FileText, AlertCircle, Plus, ArrowRight, Clock, 
  Target, Bell, Calendar, TrendingUp, CheckCircle, UserPlus, Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OperadorDashboard = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [recentConsents, setRecentConsents] = useState([]);
  const [expiringConsents, setExpiringConsents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, consentsRes, expiringRes, campaignsRes, groupsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/consents?limit=5`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/consents/expiring?days=7`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/marketing-campaigns`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/titular-groups`, { withCredentials: true })
      ]);
      
      setAnalytics(analyticsRes.data);
      setRecentConsents(Array.isArray(consentsRes.data) ? consentsRes.data.slice(0, 5) : []);
      setExpiringConsents(Array.isArray(expiringRes.data) ? expiringRes.data.slice(0, 5) : []);
      setCampaigns(Array.isArray(campaignsRes.data) ? campaignsRes.data : []);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getConsentRate = () => {
    if (!analytics?.consents?.total) return 0;
    return Math.round((analytics.consents.active / analytics.consents.total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operador-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Dashboard do Operador
          </h1>
          <p className="text-slate-500 mt-1">
            Bem-vindo, <span className="font-semibold">{user?.name}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/titulares')}>
            <UserPlus className="w-4 h-4 mr-2" />
            Novo Titular
          </Button>
          <Button onClick={() => navigate('/groups')}>
            <Target className="w-4 h-4 mr-2" />
            Nova Campanha
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/consents')}
          data-testid="op-total-consents-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Consentimentos</p>
                <p className="text-3xl font-bold text-slate-900">{analytics?.consents?.total || 0}</p>
                <p className="text-xs text-blue-500 mt-1">Ver todos →</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-l-4 border-l-green-500 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/consents')}
          data-testid="op-active-consents-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Consentimentos Ativos</p>
                <p className="text-3xl font-bold text-green-600">{analytics?.consents?.active || 0}</p>
                <p className="text-xs text-blue-500 mt-1">Ver ativos →</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-500">Taxa de Consentimento</span>
                <span className="font-medium">{getConsentRate()}%</span>
              </div>
              <Progress value={getConsentRate()} className="h-2" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/expiring-consents')}
          data-testid="op-expiring-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">A Expirar (7 dias)</p>
                <p className="text-3xl font-bold text-amber-600">{expiringConsents.length}</p>
                <p className="text-xs text-blue-500 mt-1">Ver alertas →</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-l-4 border-l-purple-500 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/groups')}
          data-testid="op-campaigns-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Campanhas Ativas</p>
                <p className="text-3xl font-bold text-purple-600">
                  {campaigns.filter(c => c.status === 'approved' || c.status === 'executed').length}
                </p>
                <p className="text-xs text-blue-500 mt-1">Ver campanhas →</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions and Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Ações Rápidas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/titulares')}>
              <UserPlus className="w-4 h-4 mr-2" />
              Registar Novo Titular
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/consents')}>
              <FileText className="w-4 h-4 mr-2" />
              Gerir Consentimentos
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/groups')}>
              <Users className="w-4 h-4 mr-2" />
              Gerir Grupos
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/expiring-consents')}>
              <Bell className="w-4 h-4 mr-2" />
              Alertas de Expiração
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/import-export')}>
              <Mail className="w-4 h-4 mr-2" />
              Importar Contactos
            </Button>
          </CardContent>
        </Card>

        {/* Groups Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Grupos de Titulares
              </span>
              <Badge variant="secondary">{groups.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {groups.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-slate-500 text-sm mb-2">Nenhum grupo criado</p>
                <Button size="sm" variant="outline" onClick={() => navigate('/groups')}>
                  <Plus className="w-4 h-4 mr-1" /> Criar Grupo
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {groups.slice(0, 4).map(group => (
                  <div key={group.group_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color || '#3B82F6' }} />
                      <span className="font-medium text-sm">{group.name}</span>
                    </div>
                    <Badge variant="outline">{group.titular_count || 0}</Badge>
                  </div>
                ))}
                {groups.length > 4 && (
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => navigate('/groups')}>
                    Ver todos ({groups.length}) <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Campaigns Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Campanhas Recentes
              </span>
              <Badge variant="secondary">{campaigns.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-slate-500 text-sm mb-2">Nenhuma campanha</p>
                <Button size="sm" variant="outline" onClick={() => navigate('/groups')}>
                  <Plus className="w-4 h-4 mr-1" /> Nova Campanha
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {campaigns.slice(0, 4).map(campaign => (
                  <div key={campaign.campaign_id} className="p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm truncate">{campaign.name}</span>
                      <Badge 
                        variant={campaign.dpo_approved ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {campaign.dpo_approved ? 'Aprovado' : 'Pendente'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {campaign.members_count || 0} membros
                    </p>
                  </div>
                ))}
                {campaigns.length > 4 && (
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => navigate('/groups')}>
                    Ver todas ({campaigns.length}) <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Consent Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Distribuição de Consentimentos</CardTitle>
            <CardDescription>Estado atual dos consentimentos registados</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div 
                className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
                onClick={() => navigate('/consents')}
                data-testid="op-active-distribution"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium">Ativos</p>
                    <p className="text-sm text-slate-500">Podem ser contactados</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">{analytics?.consents?.active || 0}</div>
                  <p className="text-xs text-blue-500">Ver →</p>
                </div>
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => navigate('/consents')}
                data-testid="op-pending-distribution"
              >
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium">Pendentes</p>
                    <p className="text-sm text-slate-500">Aguardam validação</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-amber-600">{analytics?.consents?.pending || 0}</div>
                  <p className="text-xs text-blue-500">Ver →</p>
                </div>
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => navigate('/consents')}
                data-testid="op-revoked-distribution"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium">Revogados</p>
                    <p className="text-sm text-slate-500">Não contactar</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-red-600">{analytics?.consents?.revoked || 0}</div>
                  <p className="text-xs text-blue-500">Ver →</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Métricas de Marketing
            </CardTitle>
            <CardDescription>Resumo das ações de marketing (Art. 30º RGPD)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div 
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => navigate('/groups')}
                data-testid="op-total-campaigns-metric"
              >
                <div>
                  <p className="font-medium">Total de Campanhas</p>
                  <p className="text-sm text-slate-500">Registadas na plataforma</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{campaigns.length}</div>
                  <p className="text-xs text-blue-500">Ver →</p>
                </div>
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => navigate('/groups')}
                data-testid="op-pending-approval-metric"
              >
                <div>
                  <p className="font-medium">Aguardam Aprovação DPO</p>
                  <p className="text-sm text-slate-500">Requerem validação</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-600">
                    {campaigns.filter(c => !c.dpo_approved).length}
                  </div>
                  <p className="text-xs text-blue-500">Ver →</p>
                </div>
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => navigate('/groups')}
                data-testid="op-total-groups-metric"
              >
                <div>
                  <p className="font-medium">Total de Grupos</p>
                  <p className="text-sm text-slate-500">Segmentação de titulares</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{groups.length}</div>
                  <p className="text-xs text-blue-500">Ver →</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Consents Alert */}
      {expiringConsents.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Bell className="w-5 h-5" />
              Consentimentos a Expirar
            </CardTitle>
            <CardDescription className="text-amber-600">
              {expiringConsents.length} consentimento(s) expiram nos próximos 7 dias
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {expiringConsents.slice(0, 5).map((consent, idx) => (
                <Badge key={idx} variant="outline" className="bg-white border-amber-300">
                  {consent.contact_value} - {consent.expiration_date ? new Date(consent.expiration_date).toLocaleDateString('pt-PT') : 'N/A'}
                </Badge>
              ))}
            </div>
            <Button 
              className="mt-4 bg-amber-600 hover:bg-amber-700"
              onClick={() => navigate('/expiring-consents')}
            >
              <Mail className="w-4 h-4 mr-2" />
              Gerir Alertas de Expiração
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OperadorDashboard;
